import {
  buildModalPayload,
  type ParsedModalEntry
} from '@gat-ship/vue-components'
import type { LocationQuery, LocationQueryValue } from 'vue-router'

/**
 * The legacy per-feature modal open-state params, retired in favour of the
 * single ordered `?modals=` (central-modal-host plan, 2.3). The codec sync
 * folds any present ones into `?modals=` and strips them — both as a one-time
 * redirect for bookmarks/deep links (non-negotiable #4) and continuously, so
 * existing openers can keep writing the old params.
 *
 * The admin sub-scopes `?ports-agency` / `?nodeId` are intentionally NOT here —
 * they are panel sub-state, not modal open-state, and stay as their own params
 * (the panels still read them).
 */
export const LEGACY_MODAL_PARAM_KEYS = [
  'stakeholder',
  'stakeholderType',
  'stakeholderSection',
  'tenantUser',
  'tenantUserSection',
  'agency',
  'agencySection',
  'accessRole',
  'admin',
  'settings'
] as const

function str(
  value: LocationQueryValue | LocationQueryValue[] | undefined
): string | undefined {
  const v = Array.isArray(value) ? value[0] : value
  return v == null || v === '' ? undefined : v
}

export function hasLegacyModalParams(query: LocationQuery): boolean {
  return LEGACY_MODAL_PARAM_KEYS.some((key) => str(query[key]) != null)
}

/**
 * Translate any present legacy modal params into ordered `?modals=` entries
 * (fixed precedence: detail modals before the admin/settings shells), so the
 * result is deterministic even if more than one coexists on a URL.
 */
export function legacyModalEntries(query: LocationQuery): ParsedModalEntry[] {
  const entries: ParsedModalEntry[] = []

  const stakeholder = str(query.stakeholder)
  if (stakeholder) {
    entries.push({
      kind: 'stakeholder',
      payload: buildModalPayload(stakeholder, {
        s: str(query.stakeholderSection),
        t: str(query.stakeholderType)
      })
    })
  }

  const tenantUser = str(query.tenantUser)
  if (tenantUser) {
    entries.push({
      kind: 'tenant-user',
      payload: buildModalPayload(tenantUser, {
        s: str(query.tenantUserSection)
      })
    })
  }

  const agency = str(query.agency)
  if (agency) {
    entries.push({
      kind: 'agency',
      payload: buildModalPayload(agency, { s: str(query.agencySection) })
    })
  }

  const accessRole = str(query.accessRole)
  if (accessRole) {
    entries.push({ kind: 'access-role', payload: accessRole })
  }

  const admin = str(query.admin)
  if (admin) {
    entries.push({ kind: 'admin', payload: admin })
  }

  const settings = str(query.settings)
  if (settings) {
    entries.push({ kind: 'user-settings', payload: settings })
  }

  return entries
}
